"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Check, Edit } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { OneTimePackage } from "@/types";

interface OneTimePackagesGridProps {
  packages: OneTimePackage[];
  onToggleActive: (packageId: string, active: boolean) => void;
  onEdit?: (pkg: OneTimePackage) => void;
}

export function OneTimePackagesGrid({
  packages,
  onToggleActive,
  onEdit,
}: OneTimePackagesGridProps) {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {packages.map((pkg) => (
        <Card key={pkg.id}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{pkg.name}</CardTitle>
                <p className="text-2xl font-bold mt-2">
                  {formatCurrency(pkg.price)}
                </p>
              </div>
              <Badge variant={pkg.active ? "default" : "secondary"}>
                {pkg.active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {pkg.description}
            </p>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Included Services:
              </Label>
              <ul className="space-y-1">
                {pkg.services.map((service, index) => (
                  <li
                    key={index}
                    className="text-sm flex items-start gap-2"
                  >
                    <Check className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-2 mt-4">
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(pkg)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className={onEdit ? "flex-1" : "flex-1"}
                onClick={() => onToggleActive(pkg.id, !pkg.active)}
              >
                {pkg.active ? "Deactivate" : "Activate"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

